import React, { useState, useEffect } from 'react';
import { UserLayout } from '../../components/user/UserLayout';
import { UsersRound, MapPin, Search, RefreshCw } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import axios from 'axios';
import { useToast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export const Staffing = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [staffList, setStaffList] = useState([]);
  const [sections, setSections] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredStaff, setFilteredStaff] = useState([]);
  const { toast } = useToast();

  useEffect(() => {
    const userData = localStorage.getItem('userData');
    if (userData) {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
      fetchData(parsedUser.entityId);
    }
  }, []);

  useEffect(() => {
    if (searchTerm === '') {
      setFilteredStaff(staffList);
    } else {
      const filtered = staffList.filter(staff =>
        staff.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        staff.roleName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        staff.sectionName?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredStaff(filtered);
    }
  }, [searchTerm, staffList]);

  const fetchData = async (propertyId) => {
    setLoading(true);
    try {
      // Fetch sections
      const sectionsResponse = await axios.get(`${BACKEND_URL}/api/sections/${propertyId}`);
      const sectionsData = sectionsResponse.data.success ? sectionsResponse.data.sections : [];
      setSections(sectionsData);
      const sectionMap = {};
      sectionsData.forEach(s => { sectionMap[s.id] = s.name; });

      // Fetch roles to get role IDs for Pool and Beach Attendant and Server
      const rolesResponse = await axios.get(`${BACKEND_URL}/api/roles`);
      const roles = rolesResponse.data.success ? rolesResponse.data.roles : [];
      
      const attendantRole = roles.find(r => r.name.toLowerCase().includes('pool and beach attendant'));
      const serverRole = roles.find(r => 
        r.name.toLowerCase().includes('pool and beach server') || 
        r.name.toLowerCase().includes('food and beverages server')
      );

      const roleIds = [];
      const roleMap = {};
      if (attendantRole) {
        roleIds.push(attendantRole.id);
        roleMap[attendantRole.id] = 'Pool And Beach Attendant';
      }
      if (serverRole) {
        roleIds.push(serverRole.id);
        roleMap[serverRole.id] = 'Pool And Beach Server';
      }

      // Fetch all staff for these roles
      const staffResponse = await axios.get(`${BACKEND_URL}/api/staff/${propertyId}`);
      const allStaff = staffResponse.data.success ? staffResponse.data.staff : [];
      
      // Filter to only Pool and Beach Attendant and Server roles
      const relevantStaff = allStaff.filter(s => roleIds.includes(s.roleId));

      // Fetch current section assignments
      const assignmentsResponse = await axios.get(`${BACKEND_URL}/api/staff-section-assignments/${propertyId}`);
      const assignments = assignmentsResponse.data.success ? assignmentsResponse.data.assignments : [];
      const assignmentMap = {};
      assignments.forEach(a => { assignmentMap[a.staffId] = a.sectionId; });

      // Combine data
      const staffWithAssignments = relevantStaff.map(staff => ({
        ...staff,
        roleName: roleMap[staff.roleId] || 'Unknown',
        sectionId: assignmentMap[staff.id] || null,
        sectionName: assignmentMap[staff.id] ? sectionMap[assignmentMap[staff.id]] : null
      }));

      // Sort by role then by name
      staffWithAssignments.sort((a, b) => {
        if (a.roleName !== b.roleName) {
          return a.roleName.localeCompare(b.roleName);
        }
        return a.name.localeCompare(b.name);
      });

      setStaffList(staffWithAssignments);
      setFilteredStaff(staffWithAssignments);
    } catch (error) {
      console.error('Error fetching staffing data:', error);
      toast({
        title: "Error",
        description: "Failed to load staffing data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    if (user) {
      fetchData(user.entityId);
    }
  };

  const attendants = filteredStaff.filter(s => s.roleName === 'Pool And Beach Attendant');
  const servers = filteredStaff.filter(s => s.roleName === 'Pool And Beach Server');

  return (
    <UserLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Staffing</h1>
            <p className="text-slate-600 mt-1">View staff assignments across sections</p>
          </div>
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={loading}
            className="flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Search */}
        <div className="bg-white rounded-xl shadow-md p-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
            <Input
              placeholder="Search by name, role, or section..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {loading ? (
          <div className="bg-white rounded-xl shadow-md p-12 text-center">
            <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-slate-600">Loading staff data...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Pool And Beach Attendants */}
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4">
                <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                  <UsersRound className="w-5 h-5" />
                  Pool And Beach Attendants
                  <span className="ml-auto bg-white/20 px-2 py-0.5 rounded-full text-sm">
                    {attendants.length}
                  </span>
                </h2>
              </div>
              
              {attendants.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  <UsersRound className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No attendants found</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {attendants.map((staff) => (
                    <div key={staff.id} className="px-6 py-4 hover:bg-slate-50 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center">
                            <span className="text-white font-bold text-sm">
                              {staff.name?.charAt(0)?.toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className="font-semibold text-slate-800">{staff.name}</p>
                            <p className="text-xs text-slate-500">@{staff.username}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          {staff.sectionName ? (
                            <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                              <MapPin className="w-3 h-3" />
                              {staff.sectionName}
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-3 py-1 bg-slate-100 text-slate-500 rounded-full text-sm">
                              NA
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Pool And Beach Servers */}
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
              <div className="bg-gradient-to-r from-green-500 to-green-600 px-6 py-4">
                <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                  <UsersRound className="w-5 h-5" />
                  Pool And Beach Servers
                  <span className="ml-auto bg-white/20 px-2 py-0.5 rounded-full text-sm">
                    {servers.length}
                  </span>
                </h2>
              </div>
              
              {servers.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  <UsersRound className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No servers found</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {servers.map((staff) => (
                    <div key={staff.id} className="px-6 py-4 hover:bg-slate-50 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center">
                            <span className="text-white font-bold text-sm">
                              {staff.name?.charAt(0)?.toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className="font-semibold text-slate-800">{staff.name}</p>
                            <p className="text-xs text-slate-500">@{staff.username}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          {staff.sectionName ? (
                            <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                              <MapPin className="w-3 h-3" />
                              {staff.sectionName}
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-3 py-1 bg-slate-100 text-slate-500 rounded-full text-sm">
                              NA
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Summary */}
        {!loading && (
          <div className="bg-white rounded-xl shadow-md p-6">
            <h3 className="font-semibold text-slate-800 mb-4">Summary</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-blue-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-blue-600">{attendants.length}</p>
                <p className="text-sm text-blue-600">Total Attendants</p>
              </div>
              <div className="bg-blue-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-blue-600">
                  {attendants.filter(s => s.sectionName).length}
                </p>
                <p className="text-sm text-blue-600">Attendants Assigned</p>
              </div>
              <div className="bg-green-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-green-600">{servers.length}</p>
                <p className="text-sm text-green-600">Total Servers</p>
              </div>
              <div className="bg-green-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-green-600">
                  {servers.filter(s => s.sectionName).length}
                </p>
                <p className="text-sm text-green-600">Servers Assigned</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </UserLayout>
  );
};
